import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { PastDueBreakdown } from '../podDashboard.types';
import StripeLink from './StripeLink';

interface Props {
    pastDue: PastDueBreakdown;
}

const headerCellSx = {
    color: 'text.secondary',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    border: 0,
    py: 1,
};

const bodyCellSx = { fontSize: '0.85rem', borderColor: 'grey.100', py: 1.25 };

/**
 * Past due / inactive drill-down: organizations with lapsed (canceled / past-due)
 * subscriptions that still have pods out, plus the lapsed-athlete subtotal. These
 * are the accounts to collect from or recover pods from.
 */
const PastDueDrilldown = ({ pastDue }: Props) => (
    <Box>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Org past due
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                {pastDue.orgPods.toLocaleString('en-US')} pods
            </Typography>
        </Typography>

        {pastDue.orgs.length > 0 ? (
            <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>ORGANIZATION</TableCell>
                        <TableCell sx={headerCellSx} align="right">
                            PODS OUT
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {pastDue.orgs.map((row) => (
                        <TableRow key={row.holder} hover>
                            <TableCell sx={bodyCellSx}>
                                {row.holder}
                                <StripeLink id={row.stripeCustomerId} entity="customers" />
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {row.pods.toLocaleString('en-US')}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }}>
                            Total
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {pastDue.orgPods.toLocaleString('en-US')}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
                No organizations with lapsed subscriptions.
            </Typography>
        )}

        <Typography sx={{ fontWeight: 700 }}>
            Athlete past due
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                {pastDue.athletePods.toLocaleString('en-US')} pods
            </Typography>
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, mb: 1 }}>
            Athletes whose subscription is no longer active.
        </Typography>

        {pastDue.athleteOrgs.length > 0 ? (
            pastDue.athleteOrgs.map((group) => (
                <Box key={group.holder} sx={{ mb: 2 }}>
                    <Typography
                        sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}
                    >
                        {group.holder}
                        <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                            {group.athletes.length.toLocaleString('en-US')}{' '}
                            {group.athletes.length === 1 ? 'athlete' : 'athletes'} ·{' '}
                            {group.pods.toLocaleString('en-US')} pods
                        </Typography>
                    </Typography>
                    <Table size="small" sx={{ mb: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellSx}>ATHLETE</TableCell>
                                <TableCell sx={headerCellSx} align="right">
                                    PODS OUT
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group.athletes.map((athlete, idx) => (
                                <TableRow
                                    key={athlete.individualUuid ?? `${group.holder}-${idx}`}
                                    hover
                                >
                                    <TableCell sx={bodyCellSx}>
                                        {athlete.name}
                                        <StripeLink
                                            id={athlete.stripeSubscriptionId}
                                            entity="subscriptions"
                                        />
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="right">
                                        {athlete.pods.toLocaleString('en-US')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ))
        ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                No athletes with lapsed subscriptions.
            </Typography>
        )}
    </Box>
);

export default PastDueDrilldown;
